// Shared GLSL chunks for the toon materials (§12.3), body patterns (§7.3, §12.4), identity glyphs
// (§7.2) and the dithered ghost (§12.4). Everything is procedural: no texture files.

/** Hash and value noise in 3D (cheap: 8 hashes per octave). */
export const NOISE_GLSL = /* glsl */ `
float so_hash13(vec3 p3) {
  p3 = fract(p3 * 0.1031);
  p3 += dot(p3, p3.zyx + 31.32);
  return fract((p3.x + p3.y) * p3.z);
}
float so_hash12(vec2 p) {
  vec3 p3 = fract(vec3(p.xyx) * 0.1031);
  p3 += dot(p3, p3.yzx + 33.33);
  return fract((p3.x + p3.y) * p3.z);
}
float so_noise3(vec3 p) {
  vec3 i = floor(p);
  vec3 f = fract(p);
  vec3 u = f * f * (3.0 - 2.0 * f);
  float a = so_hash13(i);
  float b = so_hash13(i + vec3(1.0, 0.0, 0.0));
  float c = so_hash13(i + vec3(0.0, 1.0, 0.0));
  float d = so_hash13(i + vec3(1.0, 1.0, 0.0));
  float e = so_hash13(i + vec3(0.0, 0.0, 1.0));
  float g = so_hash13(i + vec3(1.0, 0.0, 1.0));
  float h = so_hash13(i + vec3(0.0, 1.0, 1.0));
  float k = so_hash13(i + vec3(1.0, 1.0, 1.0));
  return mix(mix(mix(a, b, u.x), mix(c, d, u.x), u.y), mix(mix(e, g, u.x), mix(h, k, u.x), u.y), u.z);
}
float so_fbm3(vec3 p) {
  float v = 0.5 * so_noise3(p);
  v += 0.25 * so_noise3(p * 2.03 + 17.1);
  v += 0.125 * so_noise3(p * 4.11 + 3.7);
  return v / 0.875;
}
// Antialiased step: 0 below edge, 1 above, one pixel of blend.
float so_aastep(float edge, float x) {
  float w = max(fwidth(x), 1e-4) * 0.75;
  return smoothstep(edge - w, edge + w, x);
}
vec3 so_hue(vec3 c, float shift) {
  const vec3 k = vec3(0.57735);
  float cs = cos(shift * 6.2831853);
  return c * cs + cross(k, c) * sin(shift * 6.2831853) + k * dot(k, c) * (1.0 - cs);
}
`;

/**
 * Object-space body patterns (§7.3). `p` is in bean body space (y 0..1.1 up, radius ≤ 0.45, +z is
 * the face). Ids follow the catalog: 0 solid, 1 stripes, 2 polka, 3 checker, 4 gradient, 5 galaxy,
 * 6 camo, 7 tie-dye. `c1` is the body color, `c2` its auto-derived secondary.
 */
export const PATTERN_GLSL = /* glsl */ `
// Bean lathe radius at height y (the §12.4 profile points, piecewise linear).
float so_profileR(float y) {
  if (y < 0.02) return mix(0.0, 0.30, y / 0.02);
  if (y < 0.10) return mix(0.30, 0.42, (y - 0.02) / 0.08);
  if (y < 0.30) return mix(0.42, 0.45, (y - 0.10) / 0.20);
  if (y < 0.55) return mix(0.45, 0.44, (y - 0.30) / 0.25);
  if (y < 0.80) return mix(0.44, 0.40, (y - 0.55) / 0.25);
  if (y < 0.98) return mix(0.40, 0.30, (y - 0.80) / 0.18);
  if (y < 1.07) return mix(0.30, 0.16, (y - 0.98) / 0.09);
  return max(0.06, mix(0.16, 0.0, (y - 1.07) / 0.03));
}
vec3 so_pattern(vec3 p, vec3 c1, vec3 c2, float id, float time) {
  if (id < 0.5) return c1;
  float ang = atan(p.z, p.x);                         // -pi..pi around the body
  float u01 = ang / 6.2831853 + 0.5;
  if (id < 1.5) {                                      // stripes: chunky sweater bands
    float s = so_aastep(0.5, fract(p.y * 4.6 + 0.12));
    return mix(c1, c2, s);
  }
  if (id < 2.5) {                                      // polka: staggered rows, integer dots per row
    const float CELL = 0.2;
    float row = floor(p.y / CELL);
    float r = so_profileR((row + 0.5) * CELL);
    float n = max(3.0, floor(6.2831853 * r / CELL));
    float u = u01 * n + mod(row, 2.0) * 0.5;
    vec2 d = vec2((fract(u) - 0.5) * 6.2831853 * r / n, fract(p.y / CELL) - 0.5);
    d.y *= CELL;
    float dist = length(d);
    float w = max(fwidth(dist), 1e-4);
    return mix(c1, c2, 1.0 - smoothstep(0.052 - w, 0.052 + w, dist));
  }
  if (id < 3.5) {                                      // checker: even tile count per row
    const float CELL = 0.18;
    float row = floor(p.y / CELL);
    float r = so_profileR((row + 0.5) * CELL);
    float n = 2.0 * max(2.0, floor(3.14159 * r / CELL));
    float u = u01 * n;
    vec2 f = vec2(fract(u), fract(p.y / CELL)) - 0.5;
    float edge = min(0.5 - abs(f.x), 0.5 - abs(f.y));
    float parity = mod(floor(u) + row, 2.0);
    float w = max(fwidth(edge), 1e-4);
    float inner = smoothstep(0.0, w * 1.5, edge);
    float k = mix(0.5, parity, inner);
    return mix(c1, c2, k);
  }
  if (id < 4.5) {                                      // gradient: secondary at the feet to body at the top
    float t = smoothstep(0.05, 1.0, p.y);
    return mix(c2, c1, t * t * (3.0 - 2.0 * t));
  }
  if (id < 5.5) {                                      // galaxy: deep space, nebulae in body hues, stars
    vec3 q = p * 3.0;
    float n1 = so_fbm3(q + vec3(0.0, time * 0.04, 0.0));
    float n2 = so_fbm3(q * 1.9 - 5.3 + vec3(time * 0.03, 0.0, 0.0));
    vec3 space = mix(vec3(0.03, 0.02, 0.09), vec3(0.09, 0.05, 0.2), smoothstep(0.0, 1.1, p.y));
    vec3 col = mix(space, c1 * 0.9, smoothstep(0.48, 0.78, n1) * 0.9);
    col = mix(col, c2, smoothstep(0.55, 0.85, n2) * 0.75);
    col += c1 * 0.25 * smoothstep(0.7, 0.95, n1 * n2 * 1.8);
    vec3 cell = floor(p * 30.0);
    float h = so_hash13(cell);
    vec3 fp = fract(p * 30.0) - 0.5;
    float star = smoothstep(0.22, 0.0, length(fp)) * step(0.86, h);
    star *= 0.55 + 0.45 * sin(time * (2.0 + h * 5.0) + h * 40.0);
    vec3 cell2 = floor(p * 9.0);
    float h2 = so_hash13(cell2 + 7.0);
    vec3 fp2 = fract(p * 9.0) - 0.5;
    float big = smoothstep(0.1, 0.0, abs(fp2.x) * abs(fp2.y) * 40.0 + length(fp2) * 0.6) * step(0.93, h2);
    return col + vec3(1.0, 0.97, 0.9) * (star * 1.4 + big * 1.6);
  }
  if (id < 6.5) {                                      // camo: three blobby tones with clear contrast
    float n1 = so_fbm3(p * 3.6 + 2.1);
    float n2 = so_fbm3(p * 4.4 - 7.7);
    vec3 mid = mix(c1, c2, 0.5);
    vec3 col = c1;
    col = mix(col, c2, so_aastep(0.53, n1));
    col = mix(col, mid * vec3(1.05, 1.08, 0.9), so_aastep(0.58, n2) * (1.0 - so_aastep(0.53, n1)));
    return col;
  }
  // tie-dye: a swirl centered on the belly, three hues bleeding into each other
  vec3 cpos = vec3(0.0, 0.5, 0.45);
  vec3 dv = p - cpos;
  float rad = length(dv.xy * vec2(1.0, 0.9)) + max(0.0, -p.z) * 0.6;
  float a = atan(dv.y, dv.x + (p.z < 0.0 ? sign(p.x) * 0.2 : 0.0));
  float wob = so_fbm3(p * 5.0) * 1.4;
  float band = sin(a * 3.0 + rad * 16.0 + wob);
  vec3 c3 = so_hue(c1, 0.33);
  vec3 col = mix(c1, c2, smoothstep(-0.35, 0.35, band));
  col = mix(col, c3, smoothstep(0.55, 0.95, sin(a * 3.0 + rad * 16.0 + wob + 2.1)) * 0.85);
  return mix(col, vec3(1.0), smoothstep(0.85, 1.0, band) * 0.35);
}
`;

/**
 * Identity glyph signed distances (§7.2) in a unit badge space, p in [-1, 1]. Ids follow
 * IDENTITIES: 0 circle, 1 triangle, 2 square, 3 star, 4 heart, 5 diamond, 6 moon, 7 bolt.
 */
export const GLYPH_GLSL = /* glsl */ `
float so_sdSeg(vec2 p, vec2 a, vec2 b) {
  vec2 pa = p - a, ba = b - a;
  float h = clamp(dot(pa, ba) / dot(ba, ba), 0.0, 1.0);
  return length(pa - ba * h);
}
float so_sdTri(vec2 p, float r) {
  const float k = 1.7320508;
  p.x = abs(p.x) - r;
  p.y = p.y + r / k;
  if (p.x + k * p.y > 0.0) p = vec2(p.x - k * p.y, -k * p.x - p.y) / 2.0;
  p.x -= clamp(p.x, -2.0 * r, 0.0);
  return -length(p) * sign(p.y);
}
float so_sdStar5(vec2 p, float r, float rf) {
  const vec2 k1 = vec2(0.809016994375, -0.587785252292);
  const vec2 k2 = vec2(-k1.x, k1.y);
  p.x = abs(p.x);
  p -= 2.0 * max(dot(k1, p), 0.0) * k1;
  p -= 2.0 * max(dot(k2, p), 0.0) * k2;
  p.x = abs(p.x);
  p.y -= r;
  vec2 ba = rf * vec2(-k1.y, k1.x) - vec2(0, 1);
  float h = clamp(dot(p, ba) / dot(ba, ba), 0.0, r);
  return length(p - ba * h) * sign(p.y * ba.x - p.x * ba.y);
}
float so_sdHeart(vec2 p) {
  p.x = abs(p.x);
  if (p.y + p.x > 1.0) return sqrt(dot(p - vec2(0.25, 0.75), p - vec2(0.25, 0.75))) - 0.35355339;
  return sqrt(min(dot(p - vec2(0.0, 1.0), p - vec2(0.0, 1.0)), dot(p - 0.5 * max(p.x + p.y, 0.0), p - 0.5 * max(p.x + p.y, 0.0)))) * sign(p.x - p.y);
}
float so_sdRhombus(vec2 p, vec2 b) {
  p = abs(p);
  vec2 q = b - 2.0 * p;
  float h = clamp((q.x * b.x - q.y * b.y) / dot(b, b), -1.0, 1.0);
  float d = length(p - 0.5 * b * vec2(1.0 - h, 1.0 + h));
  return d * sign(p.x * b.y + p.y * b.x - b.x * b.y);
}
float so_sdBolt(vec2 p) {
  // A lightning bolt as a thick polyline.
  float d = so_sdSeg(p, vec2(0.18, 0.72), vec2(-0.22, 0.02));
  d = min(d, so_sdSeg(p, vec2(-0.22, 0.02), vec2(0.2, 0.02)));
  d = min(d, so_sdSeg(p, vec2(0.2, 0.02), vec2(-0.16, -0.72)));
  return d - 0.13;
}
float so_glyph(vec2 p, float id) {
  if (id < 0.5) return length(p) - 0.56;
  if (id < 1.5) return so_sdTri(p * vec2(1.0, -1.0) + vec2(0.0, -0.12), 0.62) - 0.06;
  if (id < 2.5) { vec2 d = abs(p) - vec2(0.44); return length(max(d, 0.0)) + min(max(d.x, d.y), 0.0) - 0.08; }
  if (id < 3.5) return so_sdStar5(vec2(p.x, -p.y + 0.06), 0.68, 0.45) - 0.04;
  if (id < 4.5) return so_sdHeart(vec2(p.x, -p.y) * 1.25 + vec2(0.0, 0.62)) / 1.25;
  if (id < 5.5) return so_sdRhombus(p, vec2(0.52, 0.72)) - 0.04;
  if (id < 6.5) { float a = length(p) - 0.62; float b = length(p - vec2(0.3, -0.18)) - 0.52; return max(a, -b); }
  return so_sdBolt(vec2(p.x, -p.y));
}
`;

/** 4×4 ordered dither threshold in (0, 1) for screen-door transparency (§12.4 ghosts). */
export const BAYER_GLSL = /* glsl */ `
float so_bayer4(vec2 p) {
  vec2 q = mod(floor(p), 4.0);
  float i = q.x + q.y * 4.0;
  float v = 0.0;
  if (i < 0.5) v = 0.0; else if (i < 1.5) v = 8.0; else if (i < 2.5) v = 2.0; else if (i < 3.5) v = 10.0;
  else if (i < 4.5) v = 12.0; else if (i < 5.5) v = 4.0; else if (i < 6.5) v = 14.0; else if (i < 7.5) v = 6.0;
  else if (i < 8.5) v = 3.0; else if (i < 9.5) v = 11.0; else if (i < 10.5) v = 1.0; else if (i < 11.5) v = 9.0;
  else if (i < 12.5) v = 15.0; else if (i < 13.5) v = 7.0; else if (i < 14.5) v = 13.0; else v = 5.0;
  return (v + 0.5) / 16.0;
}
`;

/**
 * Toon "premium gloss": a hard-edged specular blob from the key light plus a soft studio sheen
 * from the view reflection, and the §12.3 fresnel rim tinted by the map's sky-bottom color. Expects
 * `normal`, `geometryViewDir` (view space) and the directional lights of the lit materials.
 */
export const GLOSS_GLSL = /* glsl */ `
vec3 so_glossRim(vec3 n, vec3 v, float gloss, float rim, vec3 rimColor) {
  vec3 add = vec3(0.0);
  float ndv = saturate(dot(n, v));
  add += rimColor * rim * pow(1.0 - ndv, 3.0);
  #if NUM_DIR_LIGHTS > 0
  if (gloss > 0.0) {
    vec3 l = directionalLights[0].direction;
    vec3 h = normalize(l + v);
    float s = pow(saturate(dot(n, h)), 40.0 + 60.0 * gloss);
    float blob = smoothstep(0.42, 0.52, s);
    vec3 r = reflect(-v, n);
    float sheen = smoothstep(0.55, 0.95, r.y) * smoothstep(-0.2, 0.6, ndv);
    add += vec3(1.0, 0.98, 0.94) * gloss * (blob * 0.55 + sheen * 0.12);
  }
  #endif
  return add;
}
`;
